import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import priceAlertService from '../../../services/priceAlertService';
import { useAuth } from '../../../contexts/AuthContext';

const PriceAlerts = ({ alerts }) => {
  const { user } = useAuth();
  const [showAddAlert, setShowAddAlert] = useState(false);
  const [newAlert, setNewAlert] = useState({
    crypto: '',
    condition: 'above',
    price: ''
  });

  const cryptoOptions = [
    { value: 'BTC', label: 'Bitcoin (BTC)' },
    { value: 'ETH', label: 'Ethereum (ETH)' },
    { value: 'BNB', label: 'Binance Coin (BNB)' },
    { value: 'SOL', label: 'Solana (SOL)' },
    { value: 'ADA', label: 'Cardano (ADA)' }
  ];

  const conditionOptions = [
    { value: 'above', label: 'Goes Above' },
    { value: 'below', label: 'Falls Below' }
  ];

  const handleAddAlert = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }
    
    // Create price alert in database
    const result = await priceAlertService?.createPriceAlert(user?.id, {
      cryptoSymbol: newAlert?.crypto,
      cryptoName: cryptoOptions?.find(opt => opt?.value === newAlert?.crypto)?.label || newAlert?.crypto,
      condition: newAlert?.condition,
      targetPrice: parseFloat(newAlert?.price),
      currentPrice: 0 // Would be fetched from real-time price API
    });
    
    if (result?.data) {
      console.log('Price alert created:', result?.data);
    }
    
    setShowAddAlert(false);
    setNewAlert({ crypto: '', condition: 'above', price: '' });
  };

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl md:text-2xl font-bold text-foreground">Price Alerts</h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowAddAlert(!showAddAlert)}
        >
          Add Alert
        </Button>
      </div>
      {showAddAlert && (
        <div className="mb-6 p-4 bg-muted rounded-lg space-y-4">
          <Select
            label="Cryptocurrency"
            options={cryptoOptions}
            value={newAlert?.crypto}
            onChange={(value) => setNewAlert({ ...newAlert, crypto: value })}
            placeholder="Select cryptocurrency"
          />
          <Select
            label="Condition"
            options={conditionOptions}
            value={newAlert?.condition}
            onChange={(value) => setNewAlert({ ...newAlert, condition: value })}
          />
          <Input
            label="Target Price"
            type="number"
            placeholder="Enter price"
            value={newAlert?.price}
            onChange={(e) => setNewAlert({ ...newAlert, price: e?.target?.value })}
          />
          <div className="flex gap-2">
            <Button variant="default" onClick={handleAddAlert} fullWidth>
              Create Alert
            </Button>
            <Button variant="outline" onClick={() => setShowAddAlert(false)} fullWidth>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {alerts?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Bell" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm md:text-base text-muted-foreground">No active price alerts</p>
            <p className="text-xs md:text-sm text-muted-foreground mt-2">
              Create alerts to get notified when prices reach your targets
            </p>
          </div>
        ) : (
          alerts?.map((alert) => (
            <div 
              key={alert?.id} 
              className="flex items-center gap-4 p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
            >
              <Image 
                src={alert?.cryptoIcon} 
                alt={alert?.cryptoIconAlt}
                className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0"
              />

              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-semibold text-foreground">
                  {alert?.crypto}
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Alert when price {alert?.condition} ${alert?.targetPrice?.toLocaleString()}
                </p>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${alert?.active ? 'bg-success/20 text-success' : 'bg-muted-foreground/20 text-muted-foreground'}`}>
                  {alert?.active ? 'Active' : 'Inactive'}
                </div>
                <button className="p-2 hover:bg-background rounded-lg transition-colors">
                  <Icon name="Trash2" size={16} className="text-error" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PriceAlerts;